import React, { useState } from 'react';
import classNames from 'classnames';
import Spinner from '../../Spinner';
import { useDropzone } from 'react-dropzone';
import api from '../../../lib/api';

/**
 * A form for importing a JMermaid MXP-model.
 *
 * Renders a dropzone using react-dropzone
 * (https://github.com/react-dropzone/react-dropzone) and calls the backend to
 * convert the file onDrop. It that succeeds, calls the onImport function.
 *
 * Props:
 * - onImport: callback when a file was imported successfully. First argument is
 *   the filename, second argument is the imported (converted) model.
 */
interface ImportModelFormProps {
  /**
   * Callback when a file was imported successfully. First argument is the
   * filename, second argument is the imported (backend-converted) model.
   */
  onImport?: (filename: string, content: unknown) => void;
}

function ImportModelForm({ onImport }: ImportModelFormProps) {
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(false);

  /**
   * react-dropzone 'onDrop' handler
   *
   * Ask the backend to convert the file and call 'onImport' when that succeeds.
   */
  const onDrop = (files: File[]) => {
    if (!onImport) {
      // Don't bother importing if we can't pass the result somewhere.
      return;
    }
    const file = files[0];
    if (file && (!file.name || /\.mxp/i.test(file.name))) {
      setError(false);
      setImporting(true);
      api.mxp
        .importModel(file)
        .then((json) => {
          setError(false);
          setImporting(false);
          onImport(file.name, json);
        })
        .catch(() => {
          setError(true);
          setImporting(false);
        });
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    useFsAccessApi: false,
  });

  const renderError = () => {
    if (error) {
      return (
        <div className="text-danger">
          Something went wrong while converting your file. Please try again.
        </div>
      );
    }
  };

  const renderContent = () => {
    if (importing) {
      return <Spinner />;
    } else {
      return (
        <div className="h-full w-full">
          {renderError()}
          <div
            {...getRootProps({
              className: classNames(
                'flex h-full w-full cursor-pointer items-center justify-center rounded-md p-2.5 text-center align-middle text-white',
                {
                  'bg-[repeating-linear-gradient(45deg,#fff,#fff_10px,rgba(40,117,234,0.08)_10px,rgba(40,117,234,0.08)_20px)]':
                    !isDragActive,
                  'bg-[repeating-linear-gradient(45deg,#fff,#fff_10px,rgba(40,117,234,0.2)_10px,rgba(40,117,234,0.2)_20px)]':
                    isDragActive,
                },
              ),
            })}
          >
            <input {...getInputProps()} />
            <span className="rounded-md border border-grey3 bg-white p-2.5 text-black">
              Drop a JMermaid file here or click to open a file picker.
            </span>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="flex h-[100px] flex-col items-center justify-center rounded-md border border-grey3 bg-white">
      {renderContent()}
    </div>
  );
}

export default ImportModelForm;
